import React from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Box, Typography } from '@mui/material';
import { KanbanColumn, KanbanColumnData, KanbanItem } from './KanbanColumn';

export interface KanbanStage extends KanbanColumnData {
  id: string;
  name: string;
  color: string;
}

export interface KanbanBoardProps {
  stages: KanbanStage[];
  itemsByStage: Record<string, KanbanItem[]>;
  stageTotals?: Record<string, number>;
  onDragEnd: (result: DropResult) => void;
  renderCard: (item: KanbanItem, index: number) => React.ReactNode;
  itemCountLabel?: string;
  emptyStateMessage?: string;
  emptyStateSubtext?: string;
  loading?: boolean;
}

export const SharedKanbanBoard: React.FC<KanbanBoardProps> = ({
  stages,
  itemsByStage,
  stageTotals,
  onDragEnd,
  renderCard,
  itemCountLabel = 'items',
  emptyStateMessage = 'No items yet',
  emptyStateSubtext = 'Drag items here',
  loading = false,
}) => {
  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (stages.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No stages configured for this board
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          p: 3,
          overflowX: 'auto',
          minHeight: 'calc(100vh - 400px)', // Minimum height for visibility
          // Enhanced scrollbar styling
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.2)',
            },
          },
        }}
      >
        {stages.map(stage => (
          <KanbanColumn
            key={stage.id}
            column={stage}
            items={itemsByStage[stage.id] || []}
            total={stageTotals?.[stage.id]}
            itemCountLabel={itemCountLabel}
            renderCard={renderCard}
            emptyStateMessage={emptyStateMessage}
            emptyStateSubtext={emptyStateSubtext}
          />
        ))}
      </Box>
    </DragDropContext>
  );
};